import { i18n } from 'shared/i18n';

export function parseLocaleSpecificTitle(title: string, locale: string): string {
  // Creating a case-insensitive regex pattern that also correctly handles different positions of locale codes
  const pattern = new RegExp(`${locale}:(.*?)(?=[A-Z]{2}:|$)`, 'i');
  const matches = title.match(pattern);
  // If a match is found, return the first captured group; otherwise, return the original title
  const result = matches && matches[1] ? matches[1].trim() : title;
  return result;
}

type ToolbarOption = {
  selector: string;
  title: string;
};

type ToolbarDefinition = {
  options: ToolbarOption[];
  wrapperClass: string;
  templateId?: string;
};

export const toolbarCSSExtractor = (css: string | null | undefined, locale: string, type: string = 'toolbar'): ToolbarDefinition[] => {
  const toolbarClass = type === 'financial' ? '.financial-toolbar' : '.toolbar';
  if (!!css?.match) {
    // Find toolbars that are global, eg. directly below .page-content
    const globalToolbarRegex = new RegExp(`\\.page-content ${toolbarClass}\\s?{.*?(?<=}(\\n|$))`, 'gsm');

    // Find toolbars specific to a template (page structure)
    const templateToolbarRegex = new RegExp(`\\.xrp-template([-_*a-zA-Z0-9]+)?\\s?${toolbarClass}\\s?{.*?(?<=}(\\n|$))`, 'gsm');

    // Find the class wrapping .financial-toolbar (eg. .page-content or .xrp-template-XXX)
    const wrapperClassRegex = new RegExp(`(([-_a-zA-Z*])*)(?=(.*).${toolbarClass} {.*)`);

    // Extract json between "/*! { foo: "bar" } */"
    const jsonExtractorRegex = new RegExp(/(?<=\/\*!)[\s\S]*?(?=\*\/)/, 'm');

    const globalToolbar = css.match(globalToolbarRegex) ?? [];
    const templateToolbars = css.match(templateToolbarRegex)?.filter(onlyUnique) ?? []; // Duplicates can occur when page specific styling exist
    const toolbarCss = [...globalToolbar, ...templateToolbars];
    if (!!toolbarCss) {
      const toolbars: ToolbarDefinition[] = [];
      for (const toolbar of toolbarCss) {
        const wrapperClass = toolbar.match(wrapperClassRegex)?.find((className) => className) ?? '';
        let toolbarDefinition: Record<string, string> = {};

        try {
          const jsonString = toolbar.match(jsonExtractorRegex)?.[0];
          if (jsonString) {
            toolbarDefinition = JSON.parse(jsonString.replace(/[\n\r]+/g, ' '));
            for (const key in toolbarDefinition) {
              toolbarDefinition[key] = parseLocaleSpecificTitle(toolbarDefinition[key], locale);
            }
          } else {
            throw new Error('JSON string is undefined or null');
          }
        } catch (e) {
          console.warn(`Error parsing toolbar CSS (${toolbarClass}): ${(e as Error).message}`);
          dispatchToolbarWarning(toolbarClass, (e as Error).message);
          continue;
        }

        toolbars.push({
          options: Object.entries(toolbarDefinition).map(([selector, title]) => ({
            selector,
            title,
          })),
          wrapperClass,
          templateId: wrapperClass.split('xrp-template-')[1], // undefined if global
        });
      }
      return toolbars;
    }
  }
  return [];
};

let lastToolbarWarningTime = 0;
function dispatchToolbarWarning(toolbarClass: string, errorMessage: string): void {
  // Debounce warnings to avoid spamming toasts on re-renders
  const now = Date.now();
  if (now - lastToolbarWarningTime < 5000) return;
  lastToolbarWarningTime = now;

  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports -- lazy
    const { EventType, dispatchMessage } = require('shared/feedback/toasts');
    dispatchMessage(EventType.ADD_MESSAGE, {
      id: `toolbar-syntax-warning-${toolbarClass}`,
      type: 'warning',
      title: i18n('toolbar-css:syntax-error.title'),
      text: i18n('toolbar-css:syntax-error.text', { toolbarClass, error: errorMessage }),
      removeTime: 8000,
    });
  } catch {
    // Toast system not available, console.warn already logged above
  }
}

type CustomClass = {
  className: string;
  labelSv: string;
  labelEn: string;
};

type CustomClassResult = {
  classes: CustomClass[];
  error: string | null;
};

export const customClassExtractor = (css: string | null | undefined, type: string): CustomClassResult => {
  if (!css) return { classes: [], error: null };

  // New object format: /*! custom-X-classes: { "class1": "SV:Label EN:Label" } */
  const objectRegex = new RegExp(`/\\*!?\\s*custom-${type}-class(?:es)?\\s*:\\s*({[^}]*})\\s*\\*/`, 'gm');
  // Legacy array format: /*! custom-X-classes: ["class1", "class2"] */
  const strictRegex = new RegExp(`/\\*!?\\s*custom-${type}-class(?:es)?\\s*:\\s*\\[([^\\]]*)]\\s*\\*/`, 'gm');
  // Loose regex: detects any comment mentioning custom-X-class(es) (even malformed, both /* and /*!)
  const looseRegex = new RegExp(`/\\*!?[^*]*custom-${type}-class(?:es)?[^*]*\\*/`, 'gm');

  const objectMatches = [...css.matchAll(objectRegex)];
  const strictMatches = [...css.matchAll(strictRegex)];
  const looseMatches = css.match(looseRegex) || [];

  // Comment exists but neither format matched — likely a syntax error
  if (looseMatches.length > 0 && objectMatches.length === 0 && strictMatches.length === 0) {
    return {
      classes: [],
      error: `Syntax error in custom-${type}-classes. Expected format: /*! custom-${type}-classes: { "class1": "SV:Label EN:Label" } */`,
    };
  }

  const classes: CustomClass[] = [];
  const errors: string[] = [];

  // Parse new object format: returns objects with className + labels
  for (const match of objectMatches) {
    try {
      const parsed = JSON.parse(match[1]);
      for (const [className, labelStr] of Object.entries(parsed)) {
        const svMatch = String(labelStr).match(/SV:(.*?)(?=\s*EN:|$)/i);
        const enMatch = String(labelStr).match(/EN:(.*?)(?=\s*SV:|$)/i);
        classes.push({
          className,
          labelSv: svMatch ? svMatch[1].trim() : className,
          labelEn: enMatch ? enMatch[1].trim() : className,
        });
      }
    } catch (e) {
      errors.push(`Failed to parse custom-${type}-classes: ${(e as Error).message}. Check JSON syntax.`);
    }
  }

  // Parse legacy array format: returns objects with className only (no labels)
  for (const match of strictMatches) {
    try {
      const parsed = JSON.parse(`[${match[1]}]`);
      const invalid = parsed.filter((item: unknown) => typeof item !== 'string');
      if (invalid.length > 0) {
        errors.push(`Non-string values found in custom-${type}-classes: ${invalid.join(', ')}`);
      }
      for (const item of parsed) {
        if (typeof item === 'string' && !classes.some((c) => c.className === item)) {
          classes.push({ className: item, labelSv: item, labelEn: item });
        }
      }
    } catch (e) {
      errors.push(`Failed to parse custom-${type}-classes: ${(e as Error).message}. Check JSON syntax (use double quotes, no trailing commas).`);
    }
  }

  // Deduplicate by className
  const seen = new Set<string>();
  const unique = classes.filter((c) => {
    if (seen.has(c.className)) return false;
    seen.add(c.className);
    return true;
  });

  return {
    classes: unique,
    error: errors.length > 0 ? errors.join('; ') : null,
  };
};

function onlyUnique(value: string, index: number, array: string[]): boolean {
  return array.indexOf(value) === index;
}
